/**
 * Embed context set by a host before the viewer boots (JEP-10750).
 *
 * The Jepto MCP `view_report` app renders this viewer inside Claude / ChatGPT and
 * puts the tool result on `window.__JEPTO_EMBED__` before loading the bundle.
 * `viewerToken` is a `JeptoViewer` wrapper JWT minted for the report's owner so
 * private and hidden shares render without the email-code gate.
 */
export interface JeptoEmbedContext {
	shareId?: string;
	pageId?: string | null;
	viewerToken?: string;
	/** MCP `view_card` (JEP-10855): render only this card, never a report. */
	cardId?: string;
	accountId?: string;
	viewId?: string;
	cardType?: string;
	chartType?: string | null;
}

/** MCP `view_card` target. Ids are 16 hex with their entity prefix. */
export interface EmbedCard {
	accountId: string;
	viewId: string;
	pageId: string;
	cardId: string;
	cardType: string;
	chartType: string | null;
	viewerToken: string;
}

const HEX16 = /^[0-9a-f]{16}$/;

function prefixedId(value: unknown, prefix: string, field: string): string {
	const raw = String(value ?? '')
		.trim()
		.toLowerCase();
	const hex = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
	if (!HEX16.test(hex)) {
		throw new Error(`view_card embed: invalid ${field} '${raw}'`);
	}
	return `${prefix}${hex}`;
}

declare global {
	interface Window {
		__JEPTO_EMBED__?: JeptoEmbedContext;
	}
}

/** Returns the embed viewer token for `reportId`, or null when absent, missing a `shareId`, or for another report. */
export function readEmbedViewerToken(reportId: string): string | null {
	if (typeof window === 'undefined') {
		return null;
	}
	const embed = window.__JEPTO_EMBED__;
	const token = embed?.viewerToken?.trim();
	if (!token) {
		return null;
	}
	const shareId = embed?.shareId?.trim().toLowerCase();
	if (!shareId || shareId !== reportId.trim().toLowerCase()) {
		return null;
	}
	return token;
}

/**
 * The `view_card` target from `window.__JEPTO_EMBED__`, or null when the embed is not
 * a card (no `cardId`). A card embed with any missing or malformed field throws: the
 * MCP tool always sends all of them.
 */
export function readEmbedCard(): EmbedCard | null {
	if (typeof window === 'undefined') {
		return null;
	}
	const embed = window.__JEPTO_EMBED__;
	if (!embed?.cardId) {
		return null;
	}
	const cardType = embed.cardType?.trim();
	const viewerToken = embed.viewerToken?.trim();
	if (!cardType) {
		throw new Error('view_card embed: cardType is required');
	}
	if (!viewerToken) {
		throw new Error('view_card embed: viewerToken is required');
	}
	return {
		accountId: prefixedId(embed.accountId, '', 'accountId'),
		viewId: prefixedId(embed.viewId, 'view_', 'viewId'),
		pageId: prefixedId(embed.pageId, 'page_', 'pageId'),
		cardId: prefixedId(embed.cardId, 'card_', 'cardId'),
		cardType,
		chartType: embed.chartType?.trim() || null,
		viewerToken,
	};
}
